import { useRef, useState } from "react";
import { UploadCloud, FileSpreadsheet, CheckCircle2, Loader2, Download, History } from "lucide-react";
import { toast } from "sonner";
import { api, MSME_API } from "@/lib/msme-api";

export default function OpeningDisallowedUpload({ session, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!session?.id) {
      toast.error("No active session");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/sessions/${session.id}/opening-disallowed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.bill_count} opening bills · ${data.unique_ledgers} creditors`);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    if (!session?.id) return;
    window.location.href = `${MSME_API}/sessions/${session.id}/opening-disallowed-template`;
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    upload(f);
  };

  return (
    <div className="space-y-4" data-testid="opening-disallowed-upload-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl md:text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <History size={18} className="text-gray-500" /> Optional · Previous Year Disallowance
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Upload bills disallowed u/s 43B(h) in an <span className="font-medium">earlier</span> year. Any
            portion actually paid during the current year becomes allowable this year — matched automatically
            against this client&apos;s own Books JSON in the Data Library (no separate upload needed).
          </p>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          className="btn-outline-swiss flex items-center gap-2 shrink-0"
          data-testid="download-opening-disallowed-template-btn"
        >
          <Download size={14} /> Download Template
        </button>
      </div>

      <div
        className={`upload-drop p-8 text-center cursor-pointer ${drag ? "bg-gray-50 border-gray-900" : ""}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        data-testid="opening-disallowed-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => upload(e.target.files?.[0])}
          className="hidden"
          data-testid="opening-disallowed-file-input"
        />
        {busy ? (
          <div className="flex flex-col items-center gap-2 text-gray-700">
            <Loader2 className="animate-spin" />
            <span className="text-sm">Parsing Excel…</span>
          </div>
        ) : session?.has_opening_disallowed ? (
          <div className="flex flex-col items-center gap-2">
            <CheckCircle2 className="text-emerald-600" />
            <div className="font-mono text-sm">{session.opening_disallowed_count} opening bills imported</div>
            <div className="text-xs text-gray-500">{session.opening_disallowed_filename}</div>
            <div className="text-xs text-gray-500">Click to replace</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <UploadCloud className="text-gray-700" size={28} />
            <div>
              <div className="font-medium text-gray-900">Drop file or click to browse</div>
              <div className="text-xs text-gray-500 mt-1">Supports .xlsx, .xls — use the template above for a uniform format</div>
            </div>
          </div>
        )}
      </div>

      <div className="border border-gray-200 bg-gray-50 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="text-gray-700 mt-0.5" size={18} />
          <div className="text-xs text-gray-700 leading-relaxed">
            <div className="font-semibold text-gray-900 mb-1">Required columns</div>
            Ledger Name · Voucher No · Voucher Date · Bill Amount · Disallowance &gt; 45days
            <div className="text-gray-500 mt-1">Other columns from the template (Sector, MSME Type, Closing Balance, etc.) are optional and ignored.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
